import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type UserType =
  | "admin"
  | "designer"
  | "learner"
  | "other_no_course"
  | "other_one_course";

/**
 * Resolves user type for routing and feature access.
 * - admin: platform admin (user_roles).
 * - learner: only enrolled in courses, no owned project/workspace.
 * - other_no_course: not admin, no enrollments, no projects → send to course creation chat.
 * - other_one_course: not admin, no subscription, exactly one owned project → outline + download only.
 * - designer: everyone else (full designer access).
 */
export function useUserType(userId: string | undefined) {
  const { data: userType, isLoading } = useQuery({
    queryKey: ["user-type", userId ?? ""],
    queryFn: async (): Promise<UserType> => {
      if (!userId) return "designer";

      const [
        { data: adminRow },
        { data: enrollments },
        { data: projects },
        { data: courseMember },
        { data: workspace },
        { data: workspaceMember },
        { data: profile },
      ] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", userId).eq("role", "admin").limit(1).maybeSingle(),
        supabase.from("user_course_enrollments").select("id").eq("user_id", userId),
        supabase.from("learning_projects").select("id").eq("user_id", userId),
        supabase.from("course_members").select("id").eq("user_id", userId).in("role", ["owner", "editor"]).limit(1).maybeSingle(),
        supabase.from("workspaces").select("id").eq("user_id", userId).limit(1).maybeSingle(),
        supabase.from("workspace_members").select("id").eq("user_id", userId).limit(1).maybeSingle(),
        supabase.from("profiles").select("subscription_status").eq("id", userId).maybeSingle(),
      ]);

      if (adminRow?.role === "admin") return "admin";
      if (enrollments && enrollments.length > 0 && !projects?.length && !courseMember && !workspace && !workspaceMember) {
        return "learner";
      }
      const projectCount = projects?.length ?? 0;
      const hasSubscription = profile?.subscription_status === "active" || profile?.subscription_status === "trialing";
      if (projectCount === 0 && !courseMember && !workspace && !workspaceMember) return "other_no_course";
      if (projectCount === 1 && !hasSubscription && !workspace && !workspaceMember) {
        return "other_one_course";
      }
      return "designer";
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
  });

  return {
    userType: userType ?? "designer",
    isLoading: !!userId && isLoading,
    isOther: userType === "other_no_course" || userType === "other_one_course",
  };
}
